/* eslint-disable @typescript-eslint/no-explicit-any */

import React, { useEffect, useState } from "react";
import TextField from "../../inputs/TextField";
import NumberField from "../../inputs/NumberField";
import ToggleField from "../../inputs/ToggleField";
import TabPanel from "../../../ui/tabs/TabPanel";
import Form from "../../../ui/Form/Form";
import Button, { Icons } from "../../buttons/Button";
import CopyButton from "../../buttons/CopyButton";
import { EDIT_XSSI_CONNECTION_FORM_FIELDS } from "../../../constants";
import HexInput from "../../../ui/HexInput";
import {
  EDIT_ROW_TRANSLATIONS,
  EDIT_XSSI_CONNECTION_TRANSLATIONS,
  FORM_VALIDATION_TRANSLATIONS,
  SIDEPANEL_GENERAL_TRANSLATIONS,
} from "../../../translations/util";
import DeleteModal, { DeleteModalIcons } from "../../modal/DeleteModal";
import TagList from "../../tagList/TagList";
import SearchableMultiSelect from "../../searchableMultiSelect/SearchableMultiSelect";

interface IProps {
  xssiRef: string;
  connectionData: any;
  setConnectionData: any;
  updateXSSIConnections: () => void;
  resetConnectionChanges: () => void;
  loading: boolean;
  hasEditPermission: boolean;
  inCreateForm?: boolean;
  setCreateEditing?: any;
  initialConnectionData: any;
}

const isInValidNumberForField = (val?: number, min?: number, max?: number) => {
  if (val || val === 0) {
    return val < (min || 0) || val > (max || 1048575);
  }

  return true;
};

const isFormInvalid = (data: any) => {
  if (!data) return true;

  return EDIT_XSSI_CONNECTION_FORM_FIELDS.map((rows) => {
    const res = rows.fields.map((field) => {
      switch (field.type) {
        case "TEXT":
          return field.isInputInvalid?.(data[field.name]);
        case "MULTISELECT":
          return field.isInputInvalid?.(data[field.name]);
        case "HEX":
          return isInValidNumberForField(
            data[field.name],
            field.min,
            field.max
          );
        default:
          return false;
      }
    });
    return res.reduce((acc, el) => acc || el);
  }).reduce((acc, el) => acc || el);
};

const transformSelectedItems = (vocoderList: string[]) => {
  return (
    vocoderList &&
    vocoderList.map((vocoder: string) => ({
      id: vocoder,
      text: vocoder,
    }))
  );
};

const UpdateConnection = ({
  connectionData,
  setConnectionData,
  xssiRef,
  updateXSSIConnections,
  resetConnectionChanges,
  loading,
  hasEditPermission,
  inCreateForm = false,
  setCreateEditing,
  initialConnectionData,
}: IProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const [selectedItems, setSelectedItems] = useState(
    transformSelectedItems(connectionData["VOCODER-MODES"])
  );
  const handleSelection = (item: any) => {
    let newArray = [...selectedItems];
    if (newArray.find((obj) => obj.id === item.id)) {
      newArray = newArray.filter((obj) => obj.id !== item.id);
    } else {
      newArray.push(item);
    }
    setSelectedItems([...newArray]);
    setConnectionData({
      ...connectionData,
      "VOCODER-MODES": newArray.map(
        (item: { id: string; text: string }) => item.text
      ),
    });
  };
  useEffect(() => {
    if (setCreateEditing) {
      setCreateEditing(isEditing);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing]);

  return (
    <>
      <div className="data-tabcontent">
        <TabPanel>
          <div className="data-form">
            <Form>
              <div className="data-tabcontent data-card">
                <div className="tab-header">
                  <h3 data-cy="xssiConnection-heading">
                    {" "}
                    {EDIT_XSSI_CONNECTION_TRANSLATIONS.connection.heading}{" "}
                  </h3>

                  {!isEditing ? (
                    // eslint-disable-next-line react/jsx-no-useless-fragment
                    <>
                      {hasEditPermission ? (
                        <Button
                          className="w-sm"
                          kind="tertiary"
                          disabled={!hasEditPermission}
                          icon={Icons.Edit}
                          label={
                            EDIT_XSSI_CONNECTION_TRANSLATIONS.range.labels
                              .editButton
                          }
                          onClick={() => {
                            setIsEditing(true);
                          }}
                          dataCy="editXSSIConnection-"
                        />
                      ) : null}
                    </>
                  ) : null}
                </div>

                <div className="readonly-data" data-cy="xssiReadOnlySection">
                  <ul>
                    <li className="flex items-center" data-cy="referenceID">
                      <h5>
                        {" "}
                        {
                          SIDEPANEL_GENERAL_TRANSLATIONS.nonEditableFields[
                            "Reference ID"
                          ]
                        }{" "}
                      </h5>
                      <div className="data-content">
                        <span className="truncate">
                          {connectionData.Ref || xssiRef}
                        </span>
                        <CopyButton
                          copyContent={connectionData.Ref || xssiRef}
                        />
                      </div>
                    </li>

                    <li className="flex items-center" data-cy="date">
                      <div className="flex items-center mr-3">
                        <h5>
                          {" "}
                          {
                            SIDEPANEL_GENERAL_TRANSLATIONS.nonEditableFields
                              .CreationDate
                          }{" "}
                        </h5>
                        <div className="data-content">
                          <span> {connectionData.CreationDate} </span>
                        </div>
                      </div>

                      <div className="flex items-center">
                        <h5>
                          {" "}
                          {
                            SIDEPANEL_GENERAL_TRANSLATIONS.nonEditableFields
                              .ModificationDate
                          }{" "}
                        </h5>
                        <div className="data-content">
                          <span> {connectionData.ModificationDate} </span>
                        </div>
                      </div>
                    </li>

                    <li className="flex items-center " data-cy="connectionID">
                      <h5>
                        {" "}
                        {
                          SIDEPANEL_GENERAL_TRANSLATIONS.nonEditableFields[
                            "CONNECTION-ID"
                          ]
                        }{" "}
                      </h5>
                      <div className="data-content">
                        <span> {connectionData["CONNECTION-ID"]} </span>
                        <CopyButton
                          copyContent={connectionData["CONNECTION-ID"]}
                        />
                      </div>
                    </li>
                  </ul>
                </div>

                {EDIT_XSSI_CONNECTION_FORM_FIELDS.map((rows) => {
                  return (
                    <div
                      className={`grid grid-cols-${rows.cols} gap-4 form-control`}
                    >
                      {rows.fields.map((field) => {
                        switch (field.type) {
                          case "TOGGLE":
                            return (
                              <ToggleField
                                labelText={field.label}
                                onChange={(val) =>
                                  setConnectionData({
                                    ...connectionData,
                                    [field.name]: val,
                                  })
                                }
                                disabled={!isEditing}
                                value={connectionData[field.name]}
                                tooltipText={field.tooltipText}
                              />
                            );
                          case "NUMBER":
                            return (
                              <NumberField
                                onChange={(val) => {
                                  if (!val || !Number.isNaN(+val))
                                    setConnectionData({
                                      ...connectionData,
                                      [field.name]: val ? +val : undefined,
                                    });
                                }}
                                disabled={!isEditing}
                                label={field.label}
                                placeholder={field.label}
                                max={field.max || 1}
                                min={field.min || 1}
                                invalid={
                                  // connectionData[field.name] !== 0 &&
                                  !connectionData[field.name]
                                }
                                invalidText={`"${field.label}" ${FORM_VALIDATION_TRANSLATIONS.range} (${field.min}-${field.max})`}
                                value={connectionData[field.name]}
                                tooltipText={field.tooltipText}
                                required
                              />
                            );
                          case "HEX":
                            return (
                              <HexInput
                                onChange={(val) => {
                                  setConnectionData({
                                    ...connectionData,
                                    [field.name]:
                                      val || val === 0 ? +val : undefined,
                                  });
                                }}
                                disabled={!isEditing}
                                label={field.label}
                                placeholder={field.label}
                                max={field.max || 1}
                                min={field.min || 1}
                                invalid={isInValidNumberForField(
                                  connectionData[field.name],
                                  field.min,
                                  field.max
                                )}
                                invalidText={`"${field.label}" ${FORM_VALIDATION_TRANSLATIONS.range} (${field.min}-${field.max})`}
                                value={connectionData[field.name]}
                                tooltipText={field.tooltipText}
                                required
                              />
                            );
                          case "MULTISELECT":
                            return (
                              <div className="grid">
                                <SearchableMultiSelect
                                  titleText={field.label}
                                  placeholder={field.label}
                                  items={[
                                    { id: "FR", text: "FR" },
                                    { id: "HR", text: "HR" },
                                    { id: "Native", text: "Native" },
                                    { id: "Phase 1", text: "Phase 1" },
                                  ]}
                                  tooltipText={field.tooltipText}
                                  selectedItems={selectedItems}
                                  handleSelection={handleSelection}
                                  disabled={!isEditing}
                                  required
                                  clearSelected={() => {
                                    setSelectedItems([]);
                                    setConnectionData({
                                      ...connectionData,
                                      [field.name]: [],
                                    });
                                  }}
                                />
                                <TagList
                                  tagListArray={selectedItems?.map(
                                    (obj: any) => obj?.text ?? obj["CHANNEL-ID"]
                                  )}
                                  disabled={!isEditing}
                                />
                              </div>
                            );
                          default:
                            return (
                              <TextField
                                onChange={(val) =>
                                  setConnectionData({
                                    ...connectionData,
                                    [field.name]: val,
                                  })
                                }
                                disabled={!isEditing}
                                placeholder={field.label}
                                value={connectionData[field.name]}
                                invalid={field.isInputInvalid?.(
                                  connectionData[field.name]
                                )}
                                invalidText={
                                  field.invalidText ||
                                  FORM_VALIDATION_TRANSLATIONS.required(
                                    field.label
                                  )
                                }
                                labelText={field.label}
                                tooltipText={field.tooltipText}
                                required
                              />
                            );
                        }
                      })}
                    </div>
                  );
                })}

                {isEditing && !inCreateForm ? (
                  <div className="flex justify-end form-footer">
                    <div className="mr-2">
                      <Button
                        className="w-full"
                        type="button"
                        kind="tertiary"
                        disabled={false}
                        label={
                          EDIT_XSSI_CONNECTION_TRANSLATIONS.range.labels
                            .cancelButton
                        }
                        onClick={() => {
                          setOpenEdit(true);
                        }}
                        dataCy="cancelEditXssiConnection-"
                      />
                    </div>

                    <div>
                      <Button
                        className="w-full"
                        type="button"
                        loading={loading}
                        disabled={isFormInvalid(connectionData)}
                        onClick={updateXSSIConnections}
                        label={
                          EDIT_XSSI_CONNECTION_TRANSLATIONS.range.labels
                            .saveButton
                        }
                        dataCy="saveEditedXssiConnection-"
                      />
                    </div>
                  </div>
                ) : null}
              </div>
            </Form>

            {isEditing && inCreateForm ? (
              <div className="flex justify-end form-footer">
                <div className="mr-2">
                  <Button
                    className="w-full"
                    type="button"
                    kind="tertiary"
                    disabled={false}
                    label={
                      EDIT_XSSI_CONNECTION_TRANSLATIONS.range.labels
                        .discardButton
                    }
                    onClick={() => {
                      setOpenEdit(true);
                    }}
                    dataCy="discardNewXSSIConnection-"
                  />
                </div>

                <div>
                  <Button
                    className="w-full"
                    type="button"
                    loading={loading}
                    disabled={isFormInvalid(connectionData)}
                    onClick={() => {
                      updateXSSIConnections();
                    }}
                    label={
                      EDIT_XSSI_CONNECTION_TRANSLATIONS.connection.labels
                        .updateConnection
                    }
                    dataCy="updateNewXSSIConnection-"
                  />
                </div>
              </div>
            ) : null}
          </div>
        </TabPanel>
      </div>
      <DeleteModal
        modalHeaderText={`${
          EDIT_ROW_TRANSLATIONS.CANCEL_MODAL.header
        } ${"Connection"}`}
        modalDescriptionText={EDIT_ROW_TRANSLATIONS.CANCEL_MODAL.description}
        onDelete={() => {
          setIsEditing(false);
          resetConnectionChanges();
          setOpenEdit(false);
          setSelectedItems(
            transformSelectedItems(initialConnectionData["VOCODER-MODES"])
          );
        }}
        open={openEdit}
        setOpen={setOpenEdit}
        confirmationButtonLabel={
          EDIT_ROW_TRANSLATIONS.CANCEL_MODAL.confirmationButtonLabel
        }
        cancelIcon={DeleteModalIcons.Cancel}
      />
    </>
  );
};

export default UpdateConnection;
